import { Box, Grid, Typography, useTheme } from '@mui/material';
import React, { FC } from 'react';
import {
  CheckCircle,
  ErrorOutline,
  Schedule,
  Cancel,
  HelpOutline,
  CardMembership,
  QuestionMark,
} from '@mui/icons-material';
import { transformBookingCounts } from '../../../utils/bookingStatusUtils';
import { formatIndianAuto } from './echartsConfig';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

interface ReportStatusCard {
  name: string;
  value: number;
  amount?: number;
  revenue?: number;
  icon: string;
}

interface ReportStatusCardsProps {
  data: ReportStatusCard[];
}

const ReportStatusCards: FC<ReportStatusCardsProps> = ({ data }) => {
  const theme = useTheme();

  // Transform data to include icons based on name using utility function
  const transformedData = transformBookingCounts(data || []);

  const getIconComponent = (icon: string) => {
    switch (icon) {
      case 'fa fa-check-circle text-success':
        return CheckCircle;
      case 'fa fa-exclamation-circle text-info':
        return ErrorOutline;
      case 'fa fa-clock-o text-warning':
        return Schedule;
      case 'fa fa-times-circle-o text-danger':
        return Cancel;
      case 'fa fa-check-circle-o text-secondary':
        return HelpOutline;
      case 'fa fa-check-circle-o text-muted':
        return CardMembership;
      case 'fa fa-question-circle text-secondary':
        return QuestionMark;
      default:
        return CheckCircle;
    }
  };

  const getIconColor = (icon: string) => {
    if (icon.includes('text-success')) return '#66BB6A';
    if (icon.includes('text-info')) return '#42A5F5';
    if (icon.includes('text-warning')) return '#FFC107';
    if (icon.includes('text-danger')) return '#E57373';
    if (icon.includes('text-secondary')) return '#FFA726';
    if (icon.includes('text-muted')) return '#4DB6AC';
    return theme.palette.primary.main;
  };

  const formatRevenue = (amount: number) => `${getCurrencySymbol()}${formatIndianAuto(amount)}`;

  return (
    <Grid container spacing={1.5}>
      {transformedData?.map((item: ReportStatusCard, index: number) => {
        const IconComponent = getIconComponent(item.icon);
        const iconColor = getIconColor(item.icon);
        const monetary = item.revenue ?? item.amount;
        const displayValue =
          monetary != null && !Number.isNaN(monetary) ? formatRevenue(monetary) : String(item.value ?? 0);

        return (
          <Grid item key={index} xs={12} sm={6} md={3}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                py: 1.25,
                px: 1.5,
                borderRadius: 2,
                backgroundColor: theme.palette.common.white,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
                minHeight: 72,
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.action.hover,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                  <IconComponent sx={{ fontSize: 20, color: iconColor }} />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    lineHeight: 1.2,
                  }}>
                  {item.name}
                </Typography>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  lineHeight: 1.1,
                  fontSize: { xs: '1.05rem', sm: '1.2rem' },
                  ml: 5,
                }}>
                {displayValue}
              </Typography>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default ReportStatusCards;
